import React, { useState } from "react";
import FullParagraph from "segments/desktop/core-components/full-paragraph/full-paragraph";
import { ALIGN_TYPES } from "constants/align-types";
import { GRADIANT_WORKOS_IRIS_COLOR_NAME, SPACE_BLUE_COLOR_NAME, WHITE_COLOR_NAME } from "styles/color-consts";
import { lineDistance } from "utils/math";
import SignupButton from "segments/desktop/core-components/button/signup-button/signup-button";
import colors from "styles/colors";
import UniverseStars from "segments/desktop/core-components/universe-stars/universe-stars";
import classnames from "classnames";
import { stars } from "./stars-clusters-config";
import { updateUserClustersListWithMultipleClusters } from "/services/clusters-service";
import { HP_TAGS_SELECTION } from "/constants/clusters";
import { CLUSTER_TYPE, USE_CASE_TYPE } from "/constants/clusters/types";
import UniverseStarTagComponent from "./universe-star-tag/universe-star-tag-component";
import { componentStyles } from "./monday-universe-homepage-first-fold-component.scss";
import DefaultProps from "./default-props";

const Stars = React.memo(() => {
  return <UniverseStars stars={stars} />;
});

const getSelectedStarsInitialState = (clusters) => {
  const initialState = {};
  clusters.forEach((sc) => (initialState[sc.text] = false));
  return initialState;
};

const MondayUniverseHomepageFirstFoldComponent = (props) => {
  const { fullParagraph, fullParagraphAdvancedConfig, buttonConfig, clusters } = props;
  const [selectedStars, setSelectedStars] = useState(getSelectedStarsInitialState(clusters));

  const onStarTagComponentClick = (star) => {
    setSelectedStars({
      ...selectedStars,
      [star.text]: !selectedStars[star.text]
    });
  };

  const renderClusterStars = () => {
    return clusters.map((star) => {
      return (
        <div className="star-wrapper" key={star.text}>
          <UniverseStarTagComponent
            starText={star.text}
            starImage={star.image}
            starColorName={star.colorName}
            onStarClick={() => onStarTagComponentClick(star)}
            isSelected={selectedStars[star?.text]}
            isSelectableMode={true}
          />
        </div>
      );
    });
  };

  const renderLines = () => {
    const { lines } = props;
    return lines.map((line, index) => {
      const lineStart = {
        top: parseInt(line?.start?.top),
        left: parseInt(line?.start?.left)
      };
      const lineEnd = {
        top: parseInt(line?.end?.top),
        left: parseInt(line?.end?.left)
      };

      if (
        !Number.isInteger(lineStart.top) ||
        !Number.isInteger(lineStart.left) ||
        !Number.isInteger(lineEnd.top) ||
        !Number.isInteger(lineEnd.left)
      ) {
        return null;
      }

      const angle = (Math.atan2(lineEnd.top - lineStart.top, lineEnd.left - lineStart.left) * 180) / Math.PI;
      const distance = lineDistance(lineStart.left, lineStart.top, lineEnd.left, lineEnd.top);

      return (
        <div
          key={`comet-${index}`}
          className={classnames("comet-line", { underline: true })}
          style={{ transform: `rotate(${angle}deg)`, width: distance, ...lineStart }}
        />
      );
    });
  };

  const getSelectedClusters = () => {
    return clusters.filter((c) => selectedStars[c.text]);
  };

  const isAtLeastOneClusterSelected = () => {
    return Object.values(selectedStars)
      .map((star) => star)
      .reduce((acc, s) => acc || s, false);
  };

  const getButtonColorBySelectedClusters = () => {
    const { buttonColor = GRADIANT_WORKOS_IRIS_COLOR_NAME } = buttonConfig;
    const OPACITY_VALUE = "cc";

    const selectedClusters = getSelectedClusters();

    if (!isAtLeastOneClusterSelected || selectedClusters.length === 0) {
      return colors[buttonColor];
    }

    // if only 1 cluster was selected, fill the button with a gradient built of the same color
    if (selectedClusters.length === 1) {
      const colorName = selectedClusters[0].colorName;
      return `linear-gradient(90deg, ${colors[colorName]}${OPACITY_VALUE} 0%, ${colors[colorName]}${OPACITY_VALUE} 100%)`;
    }

    let colorsString = ``;
    const chunkSize = 100 / (selectedClusters.length - 1);

    // building the linear gradient string
    // for example, if 3 clusters are selected, the linear gradient stop locations should be 0%, 50% and 100%
    selectedClusters.forEach((selectedStarsItem, index) => {
      // add separator in every chunk instead of last chunk
      const separator = index === selectedClusters.length - 1 ? "" : ", ";
      colorsString += `${colors[selectedStarsItem.colorName]}${OPACITY_VALUE} ${chunkSize * index}%${separator}`;
    });

    return `linear-gradient(90deg, ${colorsString})`;
  };

  const getButtonTextColor = () => {
    const { buttonConfig } = props;
    const { buttonColor } = buttonConfig;

    const selectedClusters = getSelectedClusters();
    if (buttonColor === WHITE_COLOR_NAME && selectedClusters.length === 0) {
      return colors[SPACE_BLUE_COLOR_NAME];
    }

    return colors[WHITE_COLOR_NAME];
  };

  const saveClustersOnSignup = () => {
    const selectedClusters = getSelectedClusters();
    const clustersToUpdate = selectedClusters.map((cluster) => {
      const { clusterId, useCaseId } = cluster;
      const res = [];
      if (clusterId) {
        res.push({ clusterId, clusterType: CLUSTER_TYPE });
      }
      if (useCaseId) {
        const extraData = clusterId ? { parent_cluster_id: clusterId } : {};
        res.push({ clusterId: useCaseId, clusterType: USE_CASE_TYPE, extraData });
      }
      return res;
    });
    const flattenedArray = clustersToUpdate.flat();
    if (flattenedArray.length > 0) {
      updateUserClustersListWithMultipleClusters(flattenedArray, HP_TAGS_SELECTION);
    }
  };

  return (
    <div
      className={classnames("monday-universe-homepage-first-fold-component", {
        "selectable-clusters": true
      })}
      style={{ "--background": getButtonColorBySelectedClusters(), "--text-color": getButtonTextColor() }}
    >
      <div className="stars-canvas">
        {renderLines()}
        <Stars />
      </div>
      <div className="paragraph-wrapper">
        <FullParagraph
          {...fullParagraphAdvancedConfig}
          {...fullParagraph}
          topic={{}}
          textAlign={ALIGN_TYPES.CENTER}
          titleClassName="universe-title"
        />
      </div>
      <div className="clusters-stars">{renderClusterStars()}</div>
      {buttonConfig && (
        <div className="button-wrapper">
          <SignupButton {...buttonConfig} beforeSubmitCallback={saveClustersOnSignup} />
        </div>
      )}
      <style jsx>{componentStyles}</style>
    </div>
  );
};

MondayUniverseHomepageFirstFoldComponent.defaultProps = DefaultProps;

export default MondayUniverseHomepageFirstFoldComponent;
